import * as React from "react";
import { observer, inject } from "mobx-react";
import { Link } from "react-router-dom";
import * as Datetime from "react-datetime";
import * as moment from "moment";

import { BooleanToYesNoEmpty, YesNoEmpty, YesNoEmptyToBoolean, YesNoEmptyOptions } from "../../common/Methods/YesNoEmpty";

import { SimpleHorse, Horse, HorseSex } from "../../model/Horse";

import { HorseStore } from "../../stores/HorseStore";
import { HorseListStore } from "../../stores/HorseListStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";

import { HorsesEndpoints } from "../../endpoints/HorseEndpoints";

import LoadingWidget from "../common/LoadingWidget";
import AddHorseForSale from "./AddHorseForSale";
import { HorseLink } from "../common/HorseLink";
import HorseSelect from "../common/HorseSelect";
import NewHorseDialog, { NewHorseDialog as NewHorseDialogType } from "./NewHorseDialog";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faBan, faHorseHead, faUnlink, faLink, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { EditableSelectGroup, EditableInputGroup } from "../common/EditableCard";
import { successMessage, errorMessage } from "../common/Toast";

const NONE_ID: number = -1;
const EMPTY_DATE: string = "";

const REQUIRE_2020_REGISTRATION = new Date().getFullYear() >= 2020;
const REQUIRED_REGISTRATION_LENGTH = REQUIRE_2020_REGISTRATION ? 20 : 5;

const NoneInterceptor = (value: number) => {
    return `${value}` === `${NONE_ID}` ? null : value;
};
const ReverseNoneInterceptor = (value: number | null) => {
    return value === null ? NONE_ID : value;
};

type DamOrSire = "dam" | "sire" | null;

export interface Props {
    horseStore?: HorseStore;
    horseListStore?: HorseListStore;
    configurationStore?: ConfigurationStore;
    horsesEndpoints?: HorsesEndpoints;
}
export interface ComponentState {
    isEditable: boolean;
    isLoading: boolean;
    wasValidated: boolean;
    newHorseDialogVisible: boolean;
    newHorseIsDamOrSire: DamOrSire;
    errorMessage: string | null;
}

export interface EditableFields {
    name: string;
    dateOfFoaling: moment.Moment | null;
    embryoTransfer: YesNoEmpty;
    microchipNumber: string;
    hipNumberName: string;
    colour: number;
    sex: number;
    gait: number;
    notes: string;
    registrationNumber: string;
    sire: SimpleHorse | null;
    dam: SimpleHorse | null;
}

type State = ComponentState & EditableFields;

@inject("horseStore")
@inject("horseListStore")
@inject("horsesEndpoints")
@inject("configurationStore")
@observer
export class HorseProfile extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isEditable: false,
            isLoading: false,
            wasValidated: false,
            newHorseDialogVisible: false,
            newHorseIsDamOrSire: null,
            errorMessage: null,

            name: "",
            dateOfFoaling: null,
            microchipNumber: "",
            embryoTransfer: YesNoEmpty.EMPTY,
            hipNumberName: "",
            colour: NONE_ID,
            sex: NONE_ID,
            gait: NONE_ID,
            notes: "",
            registrationNumber: "",

            sire: null,
            dam: null
        };
    }

    handleDialogHorse = (newHorseIsDamOrSire: DamOrSire) => (): void => {
        this.setState({ newHorseIsDamOrSire, newHorseDialogVisible: true });
    };

    editFieds = () => {
        this.setState(this.copyHorseToState({ isEditable: true }) as any);
    };

    saveEditableFields = () => {
        this.props
            .horseStore!.updateFieldsByObject(this.buildFieldsToUpdate())
            .then(() => {
                successMessage(`Changes successfully saved.`);
                this.setState({ isEditable: false });
            })
            .catch(error => errorMessage(error));
    };

    cancelEditableFields = () => {
        this.setState({ isEditable: false });
    };

    handleChangeDateOfFoaling = (date: any): void => {
        if (date.format) {
            this.setState({ dateOfFoaling: date as moment.Moment });
        } else if (date === "") {
            this.setState({ sex: NONE_ID, dateOfFoaling: null });
        } else {
            // TODO We have an invalid date ... the user should hear about this
        }
    };

    handleOnChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>
            | React.ChangeEvent<HTMLSelectElement>
    ): void => {
        event.preventDefault();

        const target = event.target;
        const value = target.value;
        const name = target.name;

        const state = this.state;
        state[name] = value;
        this.setState(state);
    };

    submit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (this.formValidation()) {
            this.saveEditableFields();
            this.setState({ wasValidated: false });
        } else {
            this.setState({ wasValidated: true, isEditable: true });
        }
    };

    formValidation = (): boolean => {
        return this.state.name !== "" ? true : false;
    };

    registrationNumberHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
        let registrationNumber = event.target.value;

        if (REQUIRE_2020_REGISTRATION) {
            registrationNumber = registrationNumber.replace(/-/g, "");
            const regex = /^([^\s]{3})([^\s]{3})([^\s]{3})([^\s]{3})([^\s]{3})$/g;
            const match = regex.exec(registrationNumber);
            if (match) {
                match.shift();
                registrationNumber = match.join(" ");
            }
        }

        this.setState({ registrationNumber });
    };

    getValidDates = (currentDate: any) => {
        const today = moment().subtract(0, "day");
        return currentDate.isBefore(today);
    };

    toggleState = (name: string) => {
        const state = this.state;
        state[name] = !state[name];
        this.setState(state);
    };

    componentDidMount() {
        this.props.horseStore!.refresh();
    }

    render() {
        const { horseStore } = this.props;
        if (!horseStore) {
            throw new Error(" ** No horse Store **");
        }
        const horse = horseStore.horse;
        if (!horse) {
            return <div />;
        }

        const { configurationStore } = this.props;
        if (!configurationStore) {
            throw new Error(" ** No configuration Store **");
        }

        const currentSireOfDam = this.currentSireOfDam();
        const currentSire = this.currentSire();
        const age = this.calculateAge();
        const currentSex = this.currentSex();

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active">
                            <Link to="/horses">Horses</Link>
                        </li>
                        <li className="breadcrumb-item active" aria-current="page">
                            Horse Profile
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-12">
                            <h1>
                                <span>
                                    <FontAwesomeIcon icon={faHorseHead} size="2x" className="mr-2 color-info" />
                                </span>

                                {horse.name}
                                {this.props.horseStore!.loading && (
                                    <div className="pull-right">
                                        <LoadingWidget />
                                    </div>
                                )}
                            </h1>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className={`card shadow edit-card ${this.state.isEditable ? "editable" : "read-only"}`}>
                                <div className="card-header">
                                    <div className="row mx-0">
                                        <div className="col-8">Horse Profile</div>
                                        <div className="col-4 text-right pr-0 mr-0">
                                            {!this.state.isEditable && (
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    data-toggle="button"
                                                    aria-pressed="false"
                                                    onClick={this.editFieds}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                                                    Edit Basic Information
                                                </button>
                                            )}
                                            {this.state.isEditable && (
                                                <div>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary btn-sm mr-2"
                                                        data-toggle="button"
                                                        aria-pressed="false"
                                                        onClick={this.cancelEditableFields}
                                                    >
                                                        {" "}
                                                        <FontAwesomeIcon icon={faBan} className="mr-2" />
                                                        Cancel
                                                    </button>
                                                    <button
                                                        form="horse-form"
                                                        type="submit"
                                                        className="btn btn-primary btn-sm px-3"
                                                        data-toggle="button"
                                                        aria-pressed="false"
                                                    >
                                                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                                                        Save
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <form
                                        id="horse-form"
                                        className={"needs-validation" + (this.state.wasValidated ? " was-validated" : "")}
                                        onSubmit={e => this.submit(e)}
                                        noValidate={true}
                                    >
                                        {this.state.errorMessage && (
                                            <div className="alert alert-danger text-center" role="alert">
                                                {this.state.errorMessage}
                                            </div>
                                        )}

                                        {currentSire &&
                                            currentSire.gait &&
                                            currentSire.gait.id !== (this.state.isEditable ? this.state.gait : horse.gait && horse.gait.id) && (
                                                <div className="alert alert-warning text-center" role="alert">
                                                    <strong>Warning: </strong> The gait of this horse does not match its sire's.
                                                </div>
                                            )}

                                        <div className="form-row mb-3">
                                            <div className="col-md-4">
                                                <label htmlFor="name">
                                                    {" "}
                                                    Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    className="form-control"
                                                    onChange={this.handleOnChange}
                                                    value={this.state.isEditable ? this.state.name : horse.name}
                                                    required={true}
                                                    autoFocus={true}
                                                    readOnly={!this.state.isEditable}
                                                />
                                                <div className="invalid-feedback">Please provide a horse name.</div>
                                            </div>
                                            <EditableInputGroup
                                                label="Hip # Generation Name"
                                                className="col-md-8"
                                                type="text"
                                                name="hipNumberName"
                                                editable={this.state.isEditable && (currentSex === null || currentSex.maleFemale === "FEMALE")}
                                                isRequired={false}
                                                editableValue={this.state.hipNumberName}
                                                nonEditableValue={horse.hipNumberName}
                                                onChange={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    this.setState({ hipNumberName: e.target.value });
                                                }}
                                                smallText={
                                                    <span>
                                                        <strong style={{ color: "#0e6471" }}>Tip: </strong>This field applies to the horse you are
                                                        working on. To enter the "Hip # Generation Name" of the Dam of this horse, please do so in the
                                                        Dam’s profile or in the Application tab of any Sales for this Horse.
                                                    </span>
                                                }
                                            />
                                        </div>
                                        <div className="form-row mb-3">
                                            <div className=" col-md-4">
                                                <label htmlFor="registrationNumber">Registration #</label>
                                                <input
                                                    type="text"
                                                    name="registrationNumber"
                                                    className="form-control not-required"
                                                    aria-describedby="registrationHelpBlock"
                                                    onChange={this.registrationNumberHandler}
                                                    maxLength={REQUIRED_REGISTRATION_LENGTH}
                                                    placeholder={this.state.isEditable ? "" : "-"}
                                                    value={(this.state.isEditable ? this.state.registrationNumber : horse.registrationNumber) || ""}
                                                    readOnly={!this.state.isEditable}
                                                    required={false}
                                                />
                                            </div>
                                            <EditableInputGroup
                                                label="Microchip #"
                                                className="col-md-4"
                                                type="text"
                                                name="microchipNumber"
                                                editable={this.state.isEditable}
                                                isRequired={false}
                                                editableValue={this.state.microchipNumber}
                                                nonEditableValue={horse.microchipNumber || ""}
                                                maxLength={15}
                                                onChange={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    this.setState({ microchipNumber: e.target.value });
                                                }}
                                            />
                                        </div>
                                        <div className="form-row mb-3">
                                            <div className="form-group col-md-4">
                                                <label htmlFor="dateOfFoaling">Date of Foaling</label>
                                                <Datetime
                                                    dateFormat="MM/DD/YYYY"
                                                    inputProps={{
                                                        placeholder: "-",
                                                        disabled: !this.state.isEditable,
                                                        readOnly: !this.state.isEditable,
                                                        className: "form-control not-required"
                                                    }}
                                                    onChange={this.handleChangeDateOfFoaling}
                                                    timeFormat={false}
                                                    isValidDate={this.getValidDates || null}
                                                    value={
                                                        this.state.isEditable
                                                            ? this.state.dateOfFoaling || ""
                                                            : horse.dateOfFoaling
                                                                ? new Date(horse.dateOfFoaling)
                                                                : ""
                                                    }
                                                    closeOnSelect={true}
                                                    utc={true}
                                                />
                                            </div>

                                            <div className="form-group col-md-4">
                                                <label htmlFor="sex">Sex</label>
                                                <select
                                                    className="form-control not-required"
                                                    name="sex"
                                                    aria-describedby="sexHelpBlock"
                                                    disabled={!this.state.isEditable || this.state.dateOfFoaling === null}
                                                    onChange={this.handleOnChange}
                                                    value={this.state.isEditable ? this.state.sex : (horse.sex && horse.sex.id) || NONE_ID}
                                                >
                                                    <option key={NONE_ID} value={NONE_ID}>
                                                        {" "}
                                                        - Unknown -{" "}
                                                    </option>
                                                    {configurationStore.horsesSexes
                                                        .filter(
                                                            horseSex =>
                                                                // Make sure our current value is always included
                                                                (horse.sex && horse.sex.id === horseSex.id) ||
                                                                //  Plus all of those relevant to the current age (if an age is available)
                                                                (age !== NONE_ID &&
                                                                    (age < 4
                                                                        ? horseSex.applicableToAge !== null || horseSex.applicableFromAge === null
                                                                        : horseSex.applicableToAge === null || horseSex.applicableFromAge !== null))
                                                        )
                                                        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                                        .map(sex => (
                                                            <option key={sex.id} value={sex.id}>
                                                                {sex.name} {`(${sex.abbreviation})`}
                                                            </option>
                                                        ))}
                                                </select>
                                                {this.state.isEditable && this.calculateAge() === NONE_ID && (
                                                    <small className="form-text text-info">Select the Date of Foaling first.</small>
                                                )}
                                            </div>
                                            <EditableSelectGroup
                                                label="Embryo transfer"
                                                className="col-md-4"
                                                name="embryoTransfer"
                                                editable={this.state.isEditable}
                                                isRequired={false}
                                                editableValue={this.state.embryoTransfer}
                                                nonEditableValue={BooleanToYesNoEmpty(horse.embryoTransfer)}
                                                onChange={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    this.setState({ embryoTransfer: e.target.value });
                                                }}
                                            >
                                                {YesNoEmptyOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </EditableSelectGroup>
                                        </div>
                                        <div className="form-row mb-3">
                                            <div className="form-group col-md-4">
                                                <label htmlFor="sire">Sire: </label>
                                                {this.state.isEditable && (
                                                    <div className="row">
                                                        <HorseSelect
                                                            style="col-10"
                                                            isDisabledLink={!this.state.isEditable}
                                                            horseEndpoints={this.props.horsesEndpoints!}
                                                            showCreate={true}
                                                            include="MALE"
                                                            onCreate={this.handleDialogHorse("sire")}
                                                            value={this.state.sire}
                                                            showGait={true}
                                                            onChange={sire => this.setState({ sire, errorMessage: null })}
                                                        />
                                                        {this.state.sire && (
                                                            <button
                                                                type="button"
                                                                className="col-2 btn btn-secondary"
                                                                disabled={!this.state.isEditable}
                                                                onClick={() => {
                                                                    this.setState({ sire: null });
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {!this.state.isEditable && horse.sire && (
                                                    <div className="row">
                                                        <HorseLink className="col-10 btn btn-info btn-sm ml-3" horse={horse.sire} showGait={true} />
                                                    </div>
                                                )}
                                                {!this.state.isEditable && !horse.sire && (
                                                    <div className="row">
                                                        <div className="col-10 text-muted ml-3"> - Unknown - </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="form-group col-md-4">
                                                <label htmlFor="dam">Dam: </label>
                                                {this.state.isEditable && (
                                                    <div className="row">
                                                        <HorseSelect
                                                            style="col-10"
                                                            isDisabledLink={!this.state.isEditable}
                                                            horseEndpoints={this.props.horsesEndpoints!}
                                                            showCreate={true}
                                                            include="FEMALE"
                                                            onCreate={this.handleDialogHorse("dam")}
                                                            value={this.state.dam}
                                                            onChange={dam => this.setState({ dam })}
                                                        />
                                                        {this.state.dam && (
                                                            <button
                                                                type="button"
                                                                className="col-2 btn btn-secondary"
                                                                disabled={!this.state.isEditable}
                                                                onClick={() => {
                                                                    this.setState({ dam: null });
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {!this.state.isEditable && horse.dam && (
                                                    <div className="row">
                                                        <HorseLink className="col-10 btn btn-info btn-sm ml-3" horse={horse.dam} />
                                                    </div>
                                                )}
                                                {!this.state.isEditable && !horse.dam && (
                                                    <div className="row">
                                                        <div className="col-10 text-muted ml-3"> - Unknown - </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="form-group col-md-4">
                                                <label htmlFor="sireOfDam">Sire of Dam</label>
                                                <div className="row">
                                                    {!currentSireOfDam && <div className="col-10 text-muted ml-3"> - Unknown - </div>}
                                                    {currentSireOfDam && (
                                                        <HorseLink className="col-10 btn btn-info btn-sm ml-3" horse={currentSireOfDam} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="form-row mb-3">
                                            <div className="form-group col-md-4">
                                                <label htmlFor="gait">Gait </label>
                                                <select
                                                    className="form-control not-required"
                                                    name="gait"
                                                    disabled={!this.state.isEditable}
                                                    onChange={e => {
                                                        e.preventDefault();
                                                        this.setState({ gait: parseInt(e.target.value, 10) });
                                                    }}
                                                    value={this.state.isEditable ? this.state.gait : (horse.gait && horse.gait.id) || NONE_ID}
                                                >
                                                    <option key={NONE_ID} value={NONE_ID}>
                                                        {" "}
                                                        - Unknown -{" "}
                                                    </option>
                                                    {configurationStore.horsesGaits
                                                        .slice()
                                                        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                                        .map(gait => (
                                                            <option key={gait.id} value={gait.id}>
                                                                {gait.name} {`(${gait.abbreviation})`}
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>
                                            <div className="form-group col-md-4">
                                                <label htmlFor="colour">Colour</label>
                                                <select
                                                    className="form-control not-required"
                                                    name="colour"
                                                    disabled={!this.state.isEditable}
                                                    onChange={this.handleOnChange}
                                                    value={this.state.isEditable ? this.state.colour : (horse.colour && horse.colour.id) || NONE_ID}
                                                >
                                                    <option key={NONE_ID} value={NONE_ID}>
                                                        {" "}
                                                        - Unknown -{" "}
                                                    </option>
                                                    {configurationStore.horsesColours
                                                        .slice()
                                                        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                                        .map(colour => (
                                                            <option key={colour.id} value={colour.id}>
                                                                {colour.name} {`(${colour.abbreviation})`}
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-row mb-3">
                                            <div className="form-group col-md-12">
                                                <label htmlFor="notes">Notes: </label>
                                                <textarea
                                                    name="notes"
                                                    className="form-control not-required"
                                                    rows={5}
                                                    placeholder={this.state.isEditable ? "" : "-"}
                                                    onChange={this.handleOnChange}
                                                    value={this.state.isEditable ? this.state.notes || "" : horse.notes || ""}
                                                    readOnly={!this.state.isEditable}
                                                />
                                            </div>
                                        </div>
                                    </form>
                                </div>
                                {this.state.isEditable && (
                                    <div className="card-footer text-right">
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm mr-2"
                                            data-toggle="button"
                                            aria-pressed="false"
                                            onClick={this.cancelEditableFields}
                                        >
                                            {" "}
                                            <FontAwesomeIcon icon={faBan} className="mr-2" />
                                            Cancel
                                        </button>
                                        <button
                                            form="horse-form"
                                            type="submit"
                                            className="btn btn-primary btn-sm px-3"
                                            data-toggle="button"
                                            aria-pressed="false"
                                        >
                                            {" "}
                                            <FontAwesomeIcon icon={faSave} className="mr-2" />
                                            Save
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <NewHorseDialog
                        onCreate={(newHorse: Horse) => {
                            if (this.state.newHorseIsDamOrSire === "dam") {
                                this.setState({ dam: newHorse });
                            } else if (this.state.newHorseIsDamOrSire === "sire") {
                                this.setState({ sire: newHorse });
                            } else {
                                console.warn("Unknown type: " + this.state.newHorseIsDamOrSire);
                            }
                        }}
                        noRedirect={true}
                        horseListStore={this.props.horseListStore!}
                        configurationStore={this.props.configurationStore!}
                        show={this.state.newHorseDialogVisible}
                        onClose={() => this.setState({ newHorseDialogVisible: false })}
                    />
                    <div className="row mt-3">
                        <div className="col-12">
                            <AddHorseForSale />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private copyHorseToState(otherFields: Partial<State> = {}): Partial<State> {
        const horse = this.props.horseStore!.horse;
        if (!horse) {
            return {};
        }

        const updatedState: Partial<State> = Object.assign(
            {
                colour: (horse.colour && horse.colour.id) || NONE_ID,
                dateOfFoaling: horse.dateOfFoaling ? moment(horse.dateOfFoaling) : null,
                embryoTransfer: BooleanToYesNoEmpty(horse.embryoTransfer),
                microchipNumber: horse.microchipNumber,
                hipNumberName: horse.hipNumberName,
                gait: (horse.gait && horse.gait.id) || NONE_ID,
                name: horse.name,
                notes: horse.notes,
                registrationNumber: horse.registrationNumber,
                sex: (horse.sex && horse.sex.id) || NONE_ID,
                sire: horse.sire ? horse.sire : null,
                dam: horse.dam ? horse.dam : null
            },
            otherFields
        );

        return updatedState;
    }

    private buildFieldsToUpdate(): { [field: string]: string | number | null } {
        const horse = this.props.horseStore!.horse;
        if (!horse) {
            return {};
        }
        const obj: { [field: string]: string | number | null } = {};

        if (horse.colour) {
            if (horse.colour.id !== this.state.colour) {
                obj.colour = NoneInterceptor(this.state.colour);
            }
        } else {
            obj.colour = NoneInterceptor(this.state.colour);
        }

        if (horse.dateOfFoaling) {
            if (horse.dateOfFoaling !== (this.state.dateOfFoaling ? this.state.dateOfFoaling.valueOf() : 0)) {
                obj.dateOfFoaling = this.state.dateOfFoaling && this.state.dateOfFoaling !== null ? this.state.dateOfFoaling.valueOf() : null;
            }
        } else if (this.state.dateOfFoaling) {
            obj.dateOfFoaling = this.state.dateOfFoaling.valueOf();
        }

        const embryoTransfer = YesNoEmptyToBoolean(this.state.embryoTransfer);
        if (horse.embryoTransfer !== embryoTransfer) {
            obj.embryoTransfer = embryoTransfer === null ? null : `${embryoTransfer}`;
        }

        if (horse.hipNumberName !== this.state.hipNumberName) {
            obj.hipNumberName = this.state.hipNumberName;
        }

        if (horse.microchipNumber !== this.state.microchipNumber) {
            obj.microchipNumber = this.state.microchipNumber;
        }

        if (horse.gait) {
            if (horse.gait.id !== this.state.gait) {
                obj.gait = NoneInterceptor(this.state.gait);
            }
        } else {
            obj.gait = NoneInterceptor(this.state.gait);
        }

        if (horse.name !== this.state.name) {
            obj.name = this.state.name;
        }

        if (horse.notes !== this.state.notes) {
            obj.notes = this.state.notes;
        }

        if (horse.registrationNumber !== this.state.registrationNumber) {
            obj.registrationNumber = this.state.registrationNumber;
        }

        if (horse.sex) {
            if (horse.sex.id !== this.state.sex) {
                obj.sex = NoneInterceptor(this.state.sex);
            }
        } else {
            obj.sex = NoneInterceptor(this.state.sex);
        }

        if (horse.sire && this.state.sire) {
            if (horse.sire.id !== this.state.sire.id) {
                obj.sire = this.state.sire.id;
            }
        } else if (this.state.sire) {
            obj.sire = this.state.sire.id;
        } else {
            obj.sire = null;
        }

        if (horse.dam && this.state.dam) {
            if (horse.dam.id !== this.state.dam.id) {
                obj.dam = this.state.dam.id;
            }
        } else if (this.state.dam) {
            obj.dam = this.state.dam.id;
        } else {
            obj.dam = null;
        }

        return obj;
    }

    private currentSireOfDam(): SimpleHorse | null {
        const horseStore = this.props.horseStore!;
        if (this.state.isEditable && this.state.dam && this.state.dam.sire) {
            return this.state.dam.sire;
        } else if (!this.state.isEditable && horseStore.horse && horseStore.horse.sireOfDam) {
            return horseStore.horse.sireOfDam;
        }
        return null;
    }

    private currentSire(): SimpleHorse | null {
        const horseStore = this.props.horseStore!;
        if (this.state.isEditable && this.state.sire) {
            return this.state.sire;
        } else if (!this.state.isEditable && horseStore.horse && horseStore.horse.sire) {
            return horseStore.horse.sire;
        }
        return null;
    }

    private currentDam(): SimpleHorse | null {
        const horseStore = this.props.horseStore!;
        if (this.state.isEditable && this.state.dam) {
            return this.state.sire;
        } else if (!this.state.isEditable && horseStore.horse && horseStore.horse.dam) {
            return horseStore.horse.dam;
        }
        return null;
    }

    private currentSex(): HorseSex | null {
        const horseStore = this.props.horseStore!;
        if (this.state.isEditable && this.state.sex) {
            return this.props.configurationStore!.horsesSexes.find(sex => sex.id === parseInt("" + this.state.sex, 10)) || null;
        } else if (!this.state.isEditable && horseStore.horse && horseStore.horse.sex) {
            return horseStore.horse.sex;
        }
        return null;
    }

    private calculateAge(): number {
        const nextYear = new Date().getFullYear() + 1;
        if (this.state.isEditable) {
            if (!this.state.dateOfFoaling) {
                return NONE_ID;
            }
            return moment(`${nextYear}-01-01`).diff(this.state.dateOfFoaling, "years", false);
        } else {
            const horseStore = this.props.horseStore!;
            if (!horseStore.horse || !horseStore.horse.dateOfFoaling) {
                return NONE_ID;
            }
            return moment(`${nextYear}-01-01`).diff(horseStore.horse.dateOfFoaling, "years", false);
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/horses/HorseProfile.tsx